/**
 * @fileoverview Local-first system inspection template persistence service.
 * @module system-inspection-template-service
 *
 * Responsibilities:
 * - Provide Dexie-first CRUD operations for system inspection templates.
 * - Wake the local sync engine after optimistic writes.
 *
 * Dependencies:
 * - @firesquirrel/local-db for system Dexie access and sync wakeups.
 * - systemAuditLogService and aiFoundryService for side effects after local writes.
 *
 * Architecture:
 * - Local-first: system-web UI reads this service, this service reads Dexie first.
 * - Server reconciliation is handled by the shared push/pull sync engine.
 *
 * Notes:
 * - Normal template mutations write to Dexie and wake the shared sync engine.
 * - This service must not call HTTP APIs directly; use Dexie plus wakeLocalWrite.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { computeAuditChanges, writeAuditLog } from './systemAuditLogService';
import { generateTemplateAiMetadata } from './aiFoundryService';
import type { TemplateBuilderTemplate, TemplateType } from '@ui-admin';
import { TEMPLATE_TYPE_LABELS } from '@ui-admin';

function toAuditSnapshot(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

function remapRuleFormIds<T>(value: T, formIdRemap: ReadonlyMap<string, string>): T {
  if (Array.isArray(value)) return value.map((item) => remapRuleFormIds(item, formIdRemap)) as T;
  if (!value || typeof value !== 'object') return value;
  const next: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    next[key] = key === 'formId' && typeof raw === 'string'
      ? formIdRemap.get(raw) ?? raw
      : remapRuleFormIds(raw, formIdRemap);
  }
  return next as T;
}

/**
 * @contract SystemInspectionTemplateListItem
 *
 * Purpose:
 * - Represents the grid/list projection of a system inspection template.
 *
 * Fields:
 * - _id: Stable local identifier replicated by background sync.
 * - name: Human-readable template name rendered by grid cells.
 * - templateType: Template category key used for labels, icons, and filters.
 * - status: Draft/published/archive lifecycle state.
 * - version: Template version displayed in detail panels.
 * - requiredModules: Module dependencies required by the template.
 * - librarySettings: Publication visibility and tags.
 * - createdAt: ISO creation timestamp.
 * - updatedAt: ISO update timestamp.
 *
 * Rules:
 * - name and templateType must be scalar strings before reaching React renderers.
 *
 * Sync:
 * - Dexie is the UI source of truth; sync updates are merged by the local DB layer.
 */
export interface SystemInspectionTemplateListItem {
  _id: string;
  name: string;
  description?: string;
  templateType: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  requiredModules: string[];
  librarySettings: {
    isInLibrary: boolean;
    visibility: 'public' | 'private';
    tags: string[];
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * @contract SystemInspectionTemplate
 *
 * Purpose:
 * - Represents a full system inspection template with section/item details.
 *
 * Fields:
 * - sections: Ordered inspection content sections and questions.
 *
 * Rules:
 * - Extends the list projection used by grid and slideout surfaces.
 *
 * Sync:
 * - Loaded from Dexie only; background sync hydrates and reconciles local rows.
 */
export interface SystemInspectionTemplate extends SystemInspectionTemplateListItem {
  sections: Array<{
    id: string;
    title: string;
    order: number;
    items: Array<{
      id: string;
      question: string;
      inputType: 'pass_fail_na' | 'text' | 'number' | 'date' | 'checkbox' | 'select';
      isRequired: boolean;
      helpText?: string;
      lookupType?: string;
    }>;
  }>;
}

/**
 * @process NormalizeTemplateListItem
 *
 * Steps:
 * 1. Inspect incoming Dexie rows for legacy object-valued name payloads.
 * 2. Recover scalar name and templateType values when possible.
 * 3. Return a render-safe list item projection for React grid cells.
 *
 * Inputs:
 * - value: Raw template row from Dexie.
 *
 * Outputs:
 * - SystemInspectionTemplateListItem with scalar name and templateType.
 *
 * Failure Handling:
 * - Falls back to "Untitled Template" and "wildland" if legacy values are not recoverable.
 *
 * Idempotency:
 * - REQUIRED; normalizing an already-normalized row returns the same scalar fields.
 */
function normalizeTemplateListItem(value: any): SystemInspectionTemplateListItem {
  const embeddedName = value?.name && typeof value.name === 'object' ? value.name : null;
  const name = typeof value?.name === 'string'
    ? value.name
    : typeof embeddedName?.name === 'string'
      ? embeddedName.name
      : 'Untitled Template';
  const templateType = typeof value?.templateType === 'string'
    ? value.templateType
    : typeof embeddedName?.templateType === 'string'
      ? embeddedName.templateType
      : 'wildland';

  return {
    ...value,
    name,
    templateType,
  } as SystemInspectionTemplateListItem;
}

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - This read path does not mutate UI state.
 *
 * Sync Strategy:
 * - Return local Dexie rows only; sync refreshes Dexie outside this service.
 *
 * Conflict Resolution:
 * - Local tombstones remain filtered until sync reconciles deletion state.
 */
/**
 * @errorHandling
 *
 * Strategy:
 * - Dexie read failures propagate to the caller so UI can render an error state.
 *
 * User Impact:
 * - The grid remains usable offline from local IndexedDB state.
 *
 * Logging:
 * - REQUIRED through adjacent audit/sync systems for mutations; list reads intentionally avoid noisy logs.
 */
/**
 * @logging
 *
 * Events:
 * - Mutation audit logs are emitted by write functions; list reads do not emit per-read logs.
 *
 * Level:
 * - warn
 *
 * Correlation:
 * - MUST include template document ID for mutation paths.
 */
export async function listSystemInspectionTemplates(
  filters?: { status?: string; templateType?: string },
): Promise<SystemInspectionTemplateListItem[]> {
  return await listLocalSystemInspectionTemplates(filters);
}

/**
 * @process ListLocalSystemInspectionTemplates
 *
 * Steps:
 * 1. Read system inspection template rows from Dexie only.
 * 2. Filter tombstones and caller-provided grid filters.
 * 3. Normalize legacy malformed rows into render-safe projections.
 *
 * Inputs:
 * - filters: Optional status and template type filters.
 *
 * Outputs:
 * - Render-safe template list items from local Dexie.
 *
 * Failure Handling:
 * - Propagates Dexie read failures to the caller.
 *
 * Idempotency:
 * - REQUIRED; no sync wakeup or Dexie writes occur in this read path.
 */
/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Read-only; callers that use Dexie liveQuery must call this side-effect-free API.
 *
 * Sync Strategy:
 * - Background sync is owned by the local DB layer to avoid liveQuery write loops.
 *
 * Conflict Resolution:
 * - Not applicable for this local projection.
 */
export async function listLocalSystemInspectionTemplates(
  filters?: { status?: string; templateType?: string },
): Promise<SystemInspectionTemplateListItem[]> {
  const db = await getSystemDb();

  const localDocs = await db.system_inspection_templates.toArray();
  return (localDocs as any[])
    .filter((t: any) => !t._deleted)
    .filter((t: any) => !filters?.status || t.status === filters.status)
    .filter((t: any) => !filters?.templateType || t.templateType === filters.templateType)
    .map(normalizeTemplateListItem);
}

// ---------------------------------------------------------------------------
// Get single — Dexie only
// ---------------------------------------------------------------------------
export async function getSystemInspectionTemplate(id: string): Promise<SystemInspectionTemplate | null> {
  const db = await getSystemDb();
  const localDoc = await db.system_inspection_templates.get(id);
  return localDoc && !(localDoc as any)._deleted ? localDoc as SystemInspectionTemplate : null;
}

// ---------------------------------------------------------------------------
// Create — Dexie first, background sync
// ---------------------------------------------------------------------------
export async function createSystemInspectionTemplate(
  input: Omit<SystemInspectionTemplate, '_id' | 'createdAt' | 'updatedAt' | 'version' | 'status'>,
): Promise<SystemInspectionTemplate> {
  const now = new Date().toISOString();
  const local: SystemInspectionTemplate = {
    ...(input as any),
    _id: 'stpl-' + Math.random().toString(36).substring(2, 10),
    version: 1,
    status: 'draft',
    createdAt: now,
    updatedAt: now,
  };

  const db = await getSystemDb();
  await db.system_inspection_templates.put(local as any);
  wakeLocalWrite('system_inspection_templates');

  const after = toAuditSnapshot(local);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: local._id,
    action: 'create',
    summary: `Created system inspection template "${local.name}"`,
    changes: computeAuditChanges(null, after),
    before: null,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for template creation:', err);
  });



  return local;
}

// ---------------------------------------------------------------------------
// Update — Dexie first, background sync
// ---------------------------------------------------------------------------
export async function updateSystemInspectionTemplate(
  id: string,
  patch: Partial<SystemInspectionTemplate>,
): Promise<SystemInspectionTemplate> {
  const db = await getSystemDb();
  const existing = await db.system_inspection_templates.get(id);
  if (!existing) throw new Error(`Template ${id} not found`);

  const before = toAuditSnapshot(existing);
  const updates = { ...patch, updatedAt: new Date().toISOString() };
  const updated = { ...(existing as any), ...updates } as SystemInspectionTemplate;
  await db.system_inspection_templates.put(updated);
  wakeLocalWrite('system_inspection_templates');

  const after = toAuditSnapshot(updated);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: id,
    action: 'update',
    summary: `Updated system inspection template "${updated.name}"`,
    changes: computeAuditChanges(before, after),
    before,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for template update:', err);
  });


  return updated;
}

// ---------------------------------------------------------------------------
// Publish — Dexie first, background sync
// ---------------------------------------------------------------------------
export async function publishSystemInspectionTemplate(id: string): Promise<SystemInspectionTemplate> {
  const db = await getSystemDb();
  const existing = await db.system_inspection_templates.get(id);
  if (!existing) throw new Error(`Template ${id} not found`);

  const before = toAuditSnapshot(existing);
  const updated = {
    ...(existing as any),
    status: 'published',
    librarySettings: {
      isInLibrary: true,
      visibility: ((existing as any).librarySettings?.visibility ?? 'public') as 'public' | 'private',
      tags: Array.isArray((existing as any).librarySettings?.tags) ? (existing as any).librarySettings.tags : [],
    },
    updatedAt: new Date().toISOString(),
  } as SystemInspectionTemplate;
  await db.system_inspection_templates.put(updated);
  wakeLocalWrite('system_inspection_templates');

  const after = toAuditSnapshot(updated);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: id,
    action: 'publish',
    summary: `Published system inspection template "${updated.name}"`,
    changes: computeAuditChanges(before, after),
    before,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for template publish:', err);
  });


  // Phase 4.2: Generate aiMetadata in the background (non-blocking).
  // If the AI call succeeds, patch Dexie and wake background sync.
  generateTemplateAiMetadata({
    name: updated.name,
    description: (updated as any).description,
    templateType: updated.templateType,
    tags: (updated as any).librarySettings?.tags ?? [],
  }).then(async (meta) => {
    if (!meta) return;
    const freshDb = await getSystemDb();
    const freshExisting = await freshDb.system_inspection_templates.get(id);
    if (freshExisting) {
      await freshDb.system_inspection_templates.put({ ...(freshExisting as any), aiMetadata: meta, updatedAt: new Date().toISOString() });
      wakeLocalWrite('system_inspection_templates');
    }
  }).catch(() => { /* AI metadata is best-effort */ });

  return updated;
}

// ---------------------------------------------------------------------------
// Archive — Dexie first, background sync
// ---------------------------------------------------------------------------
export async function archiveSystemInspectionTemplate(id: string): Promise<SystemInspectionTemplate> {
  const db = await getSystemDb();
  const existing = await db.system_inspection_templates.get(id);
  if (!existing) throw new Error(`Template ${id} not found`);

  const before = toAuditSnapshot(existing);
  const updated = {
    ...(existing as any),
    status: 'archived',
    librarySettings: {
      isInLibrary: false,
      visibility: ((existing as any).librarySettings?.visibility ?? 'public') as 'public' | 'private',
      tags: Array.isArray((existing as any).librarySettings?.tags) ? (existing as any).librarySettings.tags : [],
    },
    updatedAt: new Date().toISOString(),
  } as SystemInspectionTemplate;
  await db.system_inspection_templates.put(updated);
  wakeLocalWrite('system_inspection_templates');

  const after = toAuditSnapshot(updated);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: id,
    action: 'archive',
    summary: `Archived system inspection template "${updated.name}"`,
    changes: computeAuditChanges(before, after),
    before,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for template archive:', err);
  });


  return updated;
}

// ---------------------------------------------------------------------------
// Delete (soft) — Dexie first, background sync
// ---------------------------------------------------------------------------
export async function deleteSystemInspectionTemplate(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.system_inspection_templates.get(id);
  const name = existing ? (existing as any).name ?? id : id;
  const before = toAuditSnapshot(existing);
  let after: Record<string, unknown> | null = null;

  if (existing) {
    const now = new Date().toISOString();
    const deleted = { ...(existing as any), _deleted: true, deletedAt: now, updatedAt: now };
    await db.system_inspection_templates.put(deleted);
    wakeLocalWrite('system_inspection_templates');
    after = toAuditSnapshot(deleted);
  }

  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: id,
    action: 'delete',
    summary: `Deleted system inspection template "${name}"`,
    changes: computeAuditChanges(before, after),
    before,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for template delete:', err);
  });
}

// ---------------------------------------------------------------------------
// Canvas (TemplateBuilderTemplate) CRUD — for the canvas-based editor
// ---------------------------------------------------------------------------

function generateId(): string {
  return 'stpl-' + Math.random().toString(36).substring(2, 10);
}

function generateFormId(): string {
  return 'sfrm-' + Math.random().toString(36).substring(2, 10);
}

export async function getSystemCanvasTemplate(id: string): Promise<TemplateBuilderTemplate | null> {
  const db = await getSystemDb();
  const doc = await db.system_inspection_templates.get(id);
  return doc && !(doc as any)._deleted ? doc as unknown as TemplateBuilderTemplate : null;
}


export async function createSystemCanvasTemplate(
  input: { name: string; templateType: TemplateType; description?: string },
): Promise<TemplateBuilderTemplate> {
  const now = new Date().toISOString();
  const template: TemplateBuilderTemplate = {
    _id: generateId(),
    name: input.name,
    description: input.description,
    templateType: input.templateType,
    templateTypeName: TEMPLATE_TYPE_LABELS[input.templateType] ?? input.templateType,
    version: 1,
    status: 'draft',
    formRefs: [],
    rules: [],
    parameters: {
      autoCreateReinspectionWhenViolations: false,
      selfCertificationEnabled: false,
      emailOwnerOnComplete: false,
      emailInspectorSummary: false,
      requireInspectorSignature: false,
      requireResponsiblePartySignature: false,
      allowOfflineMode: true,
    },
    createdAt: now,
    createdBy: 'system-user',
    updatedAt: now,
    updatedBy: 'system-user',
  };

  const db = await getSystemDb();
  await db.system_inspection_templates.put(template as any);
  wakeLocalWrite('system_inspection_templates');

  const after = toAuditSnapshot(template);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: template._id,
    action: 'create',
    summary: `Created system inspection template "${template.name}"`,
    changes: computeAuditChanges(null, after),
    before: null,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for canvas template creation:', err);
  });


  return template;
}

export async function updateSystemCanvasTemplate(
  id: string,
  patch: Partial<TemplateBuilderTemplate>,
): Promise<TemplateBuilderTemplate> {
  const db = await getSystemDb();
  const existing = await db.system_inspection_templates.get(id);
  if (!existing) throw new Error(`Template ${id} not found`);

  const before = toAuditSnapshot(existing);
  const updates = { ...patch, updatedAt: new Date().toISOString() };
  const updated = { ...(existing as any), ...updates } as unknown as TemplateBuilderTemplate;
  await db.system_inspection_templates.put(updated as any);
  wakeLocalWrite('system_inspection_templates');

  const after = toAuditSnapshot(updated);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: id,
    action: 'update',
    summary: `Updated system inspection template "${updated.name}"`,
    changes: computeAuditChanges(before, after),
    before,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for canvas template update:', err);
  });


  return updated;
}

/**
 * @process GetSystemDefaultTemplateParameters
 *
 * Steps:
 * 1. Build the system defaults for new inspection templates.
 * 2. Apply caller-provided overrides last.
 *
 * Inputs:
 * - overrides: Optional partial parameter values.
 *
 * Outputs:
 * - Complete parameter defaults for a TemplateBuilderTemplate.
 *
 * Failure Handling:
 * - No external failure modes; this is deterministic parameter construction.
 *
 * Idempotency:
 * - REQUIRED; same overrides produce the same output shape.
 */
export function getSystemDefaultTemplateParameters(overrides?: Partial<TemplateBuilderTemplate['parameters']>): TemplateBuilderTemplate['parameters'] {
  return {
    autoCreateReinspectionWhenViolations: false,
    selfCertificationEnabled: false,
    emailOwnerOnComplete: false,
    emailInspectorSummary: false,
    requireInspectorSignature: false,
    requireResponsiblePartySignature: false,
    allowOfflineMode: true,
    ...overrides,
  };
}

/**
 * @process CreateSystemBlankTemplate
 *
 * Steps:
 * 1. Accept the manual creation name and template type as scalar values.
 * 2. Merge optional overrides into the canvas template creation input.
 * 3. Delegate to createSystemCanvasTemplate so Dexie is written before background sync.
 *
 * Inputs:
 * - name: User-entered template name.
 * - type: Template type key.
 * - overrides: Optional TemplateBuilderTemplate fields for advanced creator flows.
 *
 * Outputs:
 * - Local-first TemplateBuilderTemplate persisted to Dexie.
 *
 * Failure Handling:
 * - Propagates createSystemCanvasTemplate failures to the caller.
 *
 * Idempotency:
 * - NOT REQUIRED; each call creates a new local template ID.
 */
/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Delegates to createSystemCanvasTemplate, which writes Dexie and wakes sync.
 *
 * Sync Strategy:
 * - wakeLocalWrite drives WebSocket/change-stream-backed sync through the shared local DB layer.
 *
 * Conflict Resolution:
 * - Fresh local IDs are pushed by the sync engine without direct API reconciliation.
 */
export async function createSystemBlankTemplate(
  name: string,
  type: TemplateType,
  overrides?: Partial<TemplateBuilderTemplate>
): Promise<TemplateBuilderTemplate> {
  return createSystemCanvasTemplate({
    name,
    templateType: type,
    description: undefined,
    ...overrides,
  });
}

/**
 * @process CreateSystemTemplateFromAIMetadata
 *
 * Steps:
 * 1. Normalize AI-provided template type into a supported system template type.
 * 2. Create the base template through the same Dexie-first path as manual creation.
 * 3. Patch AI-derived parameters and aiMetadata through the Dexie-first update path.
 *
 * Inputs:
 * - metadata: AI extraction result from the Squirrel template interview/upload flow.
 *
 * Outputs:
 * - TemplateBuilderTemplate containing AI-specific parameters and searchable metadata.
 *
 * Failure Handling:
 * - Propagates create/update errors so the caller can show creation failure UI.
 *
 * Idempotency:
 * - NOT REQUIRED; completing an AI creation flow creates a new template.
 */
/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Creates and updates through Dexie-first service functions only.
 *
 * Sync Strategy:
 * - Background sync observes the Dexie writes and pushes them to the server.
 *
 * Conflict Resolution:
 * - Server acknowledgements reconcile through local DB sync; UI keeps reading Dexie.
 */
export async function createSystemTemplateFromAIMetadata(
  metadata: any
): Promise<TemplateBuilderTemplate> {
  const templateType: TemplateType = (metadata.templateType ?? 'wildland') as TemplateType;

  const template = await createSystemCanvasTemplate({
    name: metadata.templateName,
    description: metadata.description,
    templateType,
  });

  return updateSystemCanvasTemplate(template._id, {
    parameters: {
      ...getSystemDefaultTemplateParameters(),
      autoCreateReinspectionWhenViolations: (metadata.reinspectionTriggers?.length ?? 0) > 0,
      reinspectionSchedule: [{ reinspectionNumber: 1, dueDays: 30, label: 'Follow-up' }],
      maxReinspections: 3,
      allowExtensions: true,
      allowExceptions: false,
      enableSeverity: metadata.severityTracking,
      selfCertificationEnabled: false,
      emailOwnerOnComplete: true,
      emailInspectorSummary: true,
      requireInspectorSignature: metadata.signatureRequirements?.includes('inspector'),
      requireResponsiblePartySignature: metadata.signatureRequirements?.includes('propertyOwner'),
      allowOfflineMode: metadata.offlineCapable,
    },
    aiMetadata: {
      aiDescription: metadata.description,
      aiTags: [
        metadata.templateType,
        ...(metadata.regulatoryReferences || []).map((r: string) => r.toLowerCase().replace(/[^a-z0-9]+/g, '-')),
      ],
      aiSuggestedFor: metadata.reinspectionTriggers ?? [],
      aiGeneratedAt: new Date().toISOString(),
    },
  });
}

// ---------------------------------------------------------------------------
// QA Reference Template install
// ---------------------------------------------------------------------------

/**
 * @process installQaReferenceTemplate
 *
 * Steps:
 * 1. Read the QA Reference bundle from `@firesquirrel/demo-seed`.
 * 2. Generate fresh ids for every form (so multiple installs don't collide
 *    and form-ref renaming stays decoupled across installs).
 * 3. Generate a fresh template id and rewrite `formRefs` to point at the
 *    new form ids.
 * 4. `put()` every form into `system_forms`, then `put()` the template
 *    into `system_inspection_templates`.
 * 5. Wake the sync engine so the rows replicate to the server.
 *
 * Inputs: none.
 * Outputs: `{ templateId, name }` for the freshly installed copy.
 * Idempotency: NOT REQUIRED — each call creates a new copy with new ids.
 *   Calling twice gives you two independent installs, which is the
 *   intended UX for a reference/seed template.
 *
 * @localFirst
 * Source of Truth: local Dexie (`system_inspection_templates`,
 *   `system_forms`).
 * Write Flow: Dexie put → wakeLocalWrite → background sync push.
 * Sync Strategy: standard local-first sync.
 * Conflict Resolution: not applicable (fresh ids).
 */
export async function installQaReferenceTemplate(options?: {
  /** Override the installed template's name. */
  name?: string;
}): Promise<{ templateId: string; name: string }> {
  // Lazy import so the bundle is only pulled in when actually used.
  const { getQaReferenceTemplateBundle } = await import('@firesquirrel/demo-seed');
  const bundle = getQaReferenceTemplateBundle();

  const now = new Date().toISOString();
  const installedName = options?.name ?? `${bundle.template.name} (installed ${new Date().toLocaleString()})`;

  // 1. Allocate fresh ids for every form.
  const formIdRemap = new Map<string, string>();
  for (const f of bundle.forms) formIdRemap.set(f._id, generateFormId());

  // 2. Materialize every form into Dexie under its new id. We deep-copy so
  //    the bundle's frozen JSON is never mutated.
  const db = await getSystemDb();
  const installedForms = bundle.forms.map((f) => {
    const newId = formIdRemap.get(f._id)!;
    return {
      ...remapRuleFormIds(JSON.parse(JSON.stringify(f)), formIdRemap),
      _id: newId,
      version: 1,
      status: 'draft',
      reusable: false,
      createdAt: now,
      createdBy: 'system-user',
      updatedAt: now,
      updatedBy: 'system-user',
    };
  });
  for (const f of installedForms) {
    await db.system_forms.put(f as any);
  }
  wakeLocalWrite('system_forms');

  // 3. Materialize the template with rewritten formRefs.
  const installedFormRefs = bundle.template.formRefs.map((ref) => ({
    ...ref,
    formId: formIdRemap.get(ref.formId) ?? ref.formId,
    formVersion: 1,
  }));
  const templateId = generateId();
  const installed: TemplateBuilderTemplate = {
    _id: templateId,
    name: installedName,
    description: bundle.template.description,
    templateType: 'custom' as TemplateType,
    templateTypeName: TEMPLATE_TYPE_LABELS['custom'] ?? 'Custom',
    version: 1,
    status: 'draft',
    formRefs: installedFormRefs as TemplateBuilderTemplate['formRefs'],
    rules: remapRuleFormIds((bundle.template.rules ?? []) as TemplateBuilderTemplate['rules'], formIdRemap),
    parameters: {
      autoCreateReinspectionWhenViolations: true,
      selfCertificationEnabled: false,
      emailOwnerOnComplete: false,
      emailInspectorSummary: false,
      requireInspectorSignature: false,
      requireResponsiblePartySignature: false,
      allowOfflineMode: true,
    },
    librarySettings: {
      isInLibrary: true,
      visibility: 'public',
      tags: ['qa-reference', 'demo-seed', 'all-widgets'],
    },
    createdAt: now,
    createdBy: 'system-user',
    updatedAt: now,
    updatedBy: 'system-user',
  } as unknown as TemplateBuilderTemplate;

  await db.system_inspection_templates.put(installed as any);
  wakeLocalWrite('system_inspection_templates');

  // 4. Audit log entry — same shape used by `createSystemCanvasTemplate`.
  const after = toAuditSnapshot(installed);
  writeAuditLog({
    collection: 'systemInspectionTemplates',
    documentId: templateId,
    action: 'create',
    summary: `Installed QA Reference Template "${installed.name}"`,
    changes: computeAuditChanges(null, after),
    before: null,
    after,
  }).catch((err) => {
    console.warn('[systemInspectionTemplateService] Failed to write audit log for QA install:', err);
  });

  return { templateId, name: installedName };
}

export type { TemplateBuilderTemplate };
